import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Search, Loader2, User, Check, ChevronDown, Users } from "lucide-react";
import useAxiosAdmin from "../../../lib/axiosAdmin";
import { setAllDataOfAdminStaff } from "../../../../../redux/action/admin/staff/action";

const VipStaffSelector = ({ selectedService, selectedType, selectedStaff, onSelectStaff }) => {
  const axiosAdmin = useAxiosAdmin();
  const dispatch = useDispatch();
  const { staff: staffList, loaded } = useSelector((state) => state.adminStaff);

  const [showDropdown, setShowDropdown] = useState(false);
  const [search, setSearch] = useState("");
  const [fetching, setFetching] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const listContainerRef = useRef(null);

  // --- منطق دریافت کارکنان ---
  useEffect(() => {
    if (!loaded && staffList.length === 0 && !fetching) {
      setFetching(true);
      axiosAdmin
        .get("/api/admin/staff", { params: { limit: 500 } })
        .then((res) => {
          dispatch(
            setAllDataOfAdminStaff({
              page: res.data.page || 0,
              total: res.data.total || 0,
              pages: res.data.pages || 0,
              staff: res.data.staff || [],
            })
          );
        })
        .catch((err) => console.error("خطا در دریافت کارکنان:", err))
        .finally(() => setFetching(false));
    }
  }, [loaded, staffList.length, fetching, axiosAdmin, dispatch]);

  // --- فیلتر کارکنان واجد شرایط ---
  const eligibleStaff = useMemo(() => {
    if (!selectedService || !selectedType) return [];
    return staffList.filter((member) => {
      const serviceEntry = member.services?.find(
        (s) => s.serviceId === selectedService._id
      );
      if (!serviceEntry) return false;
      if (!serviceEntry[selectedType]) return false;
      return serviceEntry.vip?.enabled === true;
    });
  }, [staffList, selectedService, selectedType]);

  // --- متن قابل جستجو برای هر کارمند (همه فیلدهای اسمی) ---
  const getSearchableText = (member) => {
    const parts = [];
    if (member.displayForAdmin) parts.push(member.displayForAdmin);
    if (member.information) {
      if (member.information.displayName) parts.push(member.information.displayName);
      if (member.information.firstName) parts.push(member.information.firstName);
      if (member.information.lastName) parts.push(member.information.lastName);
    }
    return parts.join(" ").toLowerCase();
  };

  // --- فیلتر بر اساس متن جستجو (همه فیلدها) ---
  const filteredStaff = useMemo(() => {
    if (!search.trim()) return eligibleStaff;
    const lower = search.toLowerCase().trim();
    return eligibleStaff.filter((member) => {
      return getSearchableText(member).includes(lower);
    });
  }, [eligibleStaff, search]);

  // --- مدیریت بستن دراپ‌داون ---
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // --- اسکرول به پایین لیست ---
  const scrollToBottom = useCallback(() => {
    if (listContainerRef.current) {
      listContainerRef.current.scrollTop = listContainerRef.current.scrollHeight;
    }
  }, []);

  // --- فوکوس روی جستجو ---
  useEffect(() => {
    if (showDropdown && searchInputRef.current) {
      const timeoutFocus = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timeoutFocus);
    }
  }, [showDropdown]);

  // --- اسکرول به پایین پس از رندر لیست ---
  useEffect(() => {
    if (showDropdown && listContainerRef.current) {
      const timeoutScroll = setTimeout(() => {
        scrollToBottom();
      }, 150);
      return () => clearTimeout(timeoutScroll);
    }
  }, [showDropdown, filteredStaff, scrollToBottom]);

  // --- انتخاب کارمند ---
  const handleSelect = (member) => {
    onSelectStaff(member);
    setShowDropdown(false);
    setSearch("");
  };

  // --- نام نمایشی کارمند (برای نمایش در لیست و دکمه) ---
  const displayedName = (member) => {
    if (!member) return "نامشخص";
    if (member.displayForAdmin) return member.displayForAdmin;
    if (member.information) {
      return (
        member.information.displayName ||
        `${member.information.firstName || ""} ${member.information.lastName || ""}`.trim() ||
        "نامشخص"
      );
    }
    return "نامشخص";
  };

  return (
    <div className="mb-8 mt-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* هدر بخش */}
      <div className="mb-5">
        <h3 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-bold">
            1
          </span>
          مژه‌کار (VIP)
        </h3>
        <p className="text-sm text-slate-500 mr-10">
          {eligibleStaff.length > 0
            ? `${eligibleStaff.length} مژه‌کار واجد شرایط`
            : "مژه‌کاری برای این سرویس یافت نشد"}
        </p>
      </div>

      {/* دکمه‌ی انتخاب (بسته) */}
      <div className="relative max-w-md" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          className="w-full flex items-center justify-between gap-3 border-2 border-slate-200 hover:border-purple-400 rounded-xl px-4 py-3 bg-white transition-all shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-purple-200"
        >
          <div className="flex items-center gap-3 min-w-0">
            {fetching ? (
              <Loader2 className="w-5 h-5 animate-spin text-slate-400 flex-shrink-0" />
            ) : (
              <User className="w-5 h-5 text-slate-400 flex-shrink-0" />
            )}
            <span className={`text-sm truncate ${selectedStaff ? "text-gray-900 font-medium" : "text-slate-400"}`}>
              {selectedStaff ? `${selectedStaff?.displayForAdmin ? selectedStaff.displayForAdmin : selectedStaff?.information.firstName + ' ' + selectedStaff?.information.lastName}` : "انتخاب مژه‌کار"}
            </span>
          </div>
          <ChevronDown
            className={`w-5 h-5 text-slate-400 transition-transform duration-200 ${
              showDropdown ? "rotate-180" : ""
            }`}
          />
        </button>

        {/* دراپ‌داون */}
        {showDropdown && (
          <div className="absolute z-20 mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden transition-all transform origin-top">
            {/* بخش جستجو */}
            <div className="p-3 border-b border-slate-100 bg-slate-50/50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="جستجوی نام..."
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm placeholder-slate-400 outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* لیست کارکنان */}
            <div 
              ref={listContainerRef} 
              className="max-h-56 overflow-y-auto"
              style={{ scrollBehavior: 'smooth' }}
            >
              {!loaded && fetching ? (
                <div className="p-4 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  در حال دریافت کارکنان...
                </div>
              ) : filteredStaff.length === 0 ? (
                <div className="p-6 text-center">
                  <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">
                    {search.trim() ? "نتیجه‌ای یافت نشد" : "مژه‌کاری برای این سرویس یافت نشد"}
                  </p>
                </div>
              ) : (
                filteredStaff.map((member) => {
                  const isSelected = selectedStaff?._id === member._id;
                  return (
                    <button
                      key={member._id}
                      type="button"
                      onClick={() => handleSelect(member)}
                      className={`w-full px-4 py-3 text-sm flex items-center justify-between transition-colors ${
                        isSelected
                          ? "bg-purple-50 text-purple-800 font-semibold"
                          : "hover:bg-slate-50 text-slate-700"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-purple-600" />
                        </div>
                        <span className="truncate">{member.information.firstName + ' ' + member.information.lastName}</span>
                      </div>
                      {isSelected && (
                        <Check className="w-5 h-5 text-purple-600 flex-shrink-0 ml-2" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VipStaffSelector;